import {
  TextInput as MantineTextInput,
  TextInputProps as MantineTextInputProps,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { Box, IBoxProps } from '@zorro/web/design-system';
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';

import styles from './Search.module.css';

export type SearchMode = 'regular' | 'closed';
export type SearchSize = 'sm' | 'md' | 'lg';
export type SearchVariant = 'default' | 'global';

export interface SearchProps extends Omit<
  MantineTextInputProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'leftSection'
  | 'leftSectionWidth'
  | 'type'
  | 'label'
  | 'error'
  | 'description'
  | 'required'
  | 'withAsterisk'
  | 'inputWrapperOrder'
  | 'readOnly'
> {
  /** Visual variant: 'default' (white bg with border) or 'global' (gray bg, no border). Default: 'default' */
  variant?: SearchVariant;
  /** 'regular' = always expanded, 'closed' = collapsible icon. Default: 'regular' */
  mode?: SearchMode;
  /** Called when clear button is clicked or Escape key is pressed */
  onClear?: () => void;
  /** Whether to show clear button when input has value. Default: true */
  isClearable?: boolean;
  isReadOnly?: boolean;
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: SearchSize;
  rootProps?: IBoxProps;
}

export const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      variant = 'default',
      mode = 'regular',
      size = 'md',
      value,
      onChange,
      onClear,
      onKeyDown,
      onFocus,
      onBlur,
      isClearable = true,
      disabled,
      isReadOnly,
      rightSection,
      rightSectionWidth,
      placeholder = 'Search',
      'aria-label': ariaLabel = 'Search',
      rootProps,
      ...props
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(mode === 'regular');
    const [internalHasValue, setInternalHasValue] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const isControlled = value !== undefined;

    const handleRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node;
        }
      },
      [ref]
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setInternalHasValue(!!event.currentTarget.value);
        onChange?.(event);
      },
      [onChange]
    );

    const handleClear = useCallback(() => {
      if (!isControlled && inputRef.current) {
        inputRef.current.value = '';
      }
      setInternalHasValue(false);
      onClear?.();
      inputRef.current?.focus();
    }, [onClear, isControlled]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          if (event.currentTarget.value) {
            handleClear();
          } else if (mode === 'closed') {
            setIsExpanded(false);
            event.currentTarget.blur();
          }
          event.preventDefault();
        }
        onKeyDown?.(event);
      },
      [handleClear, onKeyDown, mode]
    );

    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (mode === 'closed') {
          setIsExpanded(true);
        }
        onFocus?.(event);
      },
      [mode, onFocus]
    );

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (mode === 'closed' && !event.currentTarget.value) {
          setIsExpanded(false);
        }
        onBlur?.(event);
      },
      [mode, onBlur]
    );

    const handleRootClick = useCallback(() => {
      if (mode === 'closed' && !isExpanded && !disabled) {
        setIsExpanded(true);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    }, [mode, isExpanded, disabled]);

    const preventBlur = useCallback((event: MouseEvent) => {
      event.preventDefault();
    }, []);

    const hasValue = isControlled ? !!value : internalHasValue;
    const showClearButton = isClearable && hasValue && !disabled && !isReadOnly;

    const clearButton = showClearButton ? (
      <button
        type="button"
        className={styles.clearButton}
        onMouseDown={preventBlur}
        onClick={handleClear}
        tabIndex={-1}
        aria-label="Clear search"
      >
        <IconX size={16} stroke={2} />
      </button>
    ) : undefined;

    return (
      <Box
        {...rootProps}
        className={styles.root}
        data-mode={mode}
        data-expanded={isExpanded}
        data-input-size={size}
        data-variant={variant}
        onClick={handleRootClick}
      >
        <MantineTextInput
          {...props}
          ref={handleRef}
          unstyled
          classNames={{
            root: styles.mantineRoot,
            wrapper: styles.wrapper,
            input: styles.input,
            section: styles.section,
          }}
          type="search"
          aria-label={ariaLabel}
          leftSection={<IconSearch size={20} stroke={2} />}
          leftSectionWidth={36}
          rightSection={rightSection ?? clearButton}
          rightSectionWidth={rightSectionWidth}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={isReadOnly}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </Box>
    );
  }
);

Search.displayName = 'Search';
